<template>
  <div class="flex flex-col -mt-4 h-full min-h-0">
    <!-- Header -->
    <div class="pb-3 border-b border-gray-700 shrink-0">
      <h2 class="text-lg font-semibold text-white">Change Review Image</h2>
      <p class="mt-1 text-sm text-gray-400">
        Select and adjust the image for your review
      </p>
    </div>

    <!-- Image Editor -->
    <div v-if="title" class="overflow-y-auto flex-1 py-4 min-h-0">
      <ReviewImageEditor
        ref="imageEditorRef"
        :images="title?.images?.backdrops || []"
        :initial-index="currentIndex"
        :score-color="scoreColorComputed"
        :title-text="titleTextComputed"
        :username="currentUsername"
        :score="scoreOverallComputed"
        :year="titleYearComputed"
        :initial-transform="existingTransform"
        :initial-image-path="existingImagePath"
        @update:image-path="onImagePathUpdate" />
    </div>
    <div v-else class="flex flex-1 justify-center items-center">
      <div class="text-center">
        <fa-icon
          icon="spinner-third"
          class="mb-2 text-2xl text-gray-400 animate-spin" />
        <p class="text-gray-400">Loading title details...</p>
      </div>
    </div>

    <!-- Footer: Action Buttons -->
    <div class="px-0 pt-4 border-t border-gray-700 shrink-0">
      <div class="flex gap-x-4 justify-end">
        <button
          type="button"
          class="flex justify-center items-center py-3 px-4 text-sm font-semibold text-gray-300 bg-gray-700 rounded-lg border border-gray-600 transition ease-in-out hover:bg-gray-600"
          @click="closeModal">
          Cancel
        </button>
        <button
          type="button"
          :disabled="formBusy"
          class="flex justify-center items-center py-3 px-4 text-sm font-semibold rounded-lg border border-transparent transition-all duration-300 disabled:pointer-events-none disabled:opacity-70"
          :class="[
            'bg-linear-to-r from-sky-500 to-indigo-500 hover:from-sky-400 hover:to-indigo-400 text-white',
            formBusy ? 'animate-pulse' : ''
          ]"
          @click.prevent="handleSubmit">
          <template v-if="formBusy">
            <span class="flex items-center">
              <svg
                class="mr-2 -ml-1 w-4 h-4 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24">
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Updating...
            </span>
          </template>
          <template v-else>
            Update Image <fa-icon icon="check" class="ml-2" />
          </template>
        </button>
      </div>

      <div
        v-if="formError"
        class="p-2 mt-2 text-xs rounded-sm cursor-pointer bg-red-500/80 hover:bg-red-600"
        @click.prevent="clearFormError">
        Error updating image: {{ formErrorMessage }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { useStoreTMDB } from '@/stores/storeTMDB'
import { useStoreReviews } from '@/stores/storeReviews'
import { useStoreProfile } from '@/stores/storeProfile'
import { useStoreAuth } from '@/stores/storeAuth'
import { useReviewColors } from '@/use/useReviewColors'
import ReviewImageEditor from '@/components/Reviews/ReviewImageEditor.vue'

const props = defineProps({
  review: Object
})

const emit = defineEmits(['close'])

const storeTMDB = useStoreTMDB()
const storeReviews = useStoreReviews()
const storeProfile = useStoreProfile()
const storeAuth = useStoreAuth()
const { scoreHsl } = useReviewColors()

const formBusy = ref(false)
const formError = ref(null)
const formErrorMessage = ref(null)
const imageEditorRef = ref(null)
const currentIndex = ref(0)
const selectedImagePath = ref(null)

// Get title data
const title = computed(() => {
  if (!props.review?.tmdb_id || !props.review?.tmdb_type) return null
  return storeTMDB.getTitleById(props.review.tmdb_id, props.review.tmdb_type)
})

// Fetch title if not already loaded
watch(
  () => props.review?.tmdb_id,
  (tmdbId) => {
    if (tmdbId && props.review?.tmdb_type) {
      storeTMDB.fetchTitleById(props.review.tmdb_id, props.review.tmdb_type)
    }
  },
  { immediate: true }
)

// Find current image index
watch(
  () => [title.value?.images?.backdrops, props.review?.image_path],
  ([backdrops, imagePath]) => {
    if (backdrops && imagePath) {
      const index = backdrops.findIndex((img) => img.file_path === imagePath)
      if (index >= 0) {
        currentIndex.value = index
      }
    }
  },
  { immediate: true }
)

// Get existing transform data
const existingTransform = computed(() => {
  if (!props.review?.image_transform) return null
  return props.review.image_transform
})

// Get existing image path
const existingImagePath = computed(() => {
  if (!props.review?.image_path) return null
  return props.review.image_path
})

// Compute score color
const scoreColorComputed = computed(() => {
  const score = props.review?.score_overall ?? 5
  return scoreHsl(parseFloat(score))
})

// Get current user's username
const currentUsername = computed(() => {
  const userId = storeAuth.user?.id
  if (!userId) return ''
  const profile = storeProfile.getProfile(userId)
  return profile?.username || ''
})

// Get title text
const titleTextComputed = computed(() => {
  return title.value?.title || title.value?.name || ''
})

// Get release year
const titleYearComputed = computed(() => {
  const releaseDate = title.value?.release_date || title.value?.first_air_date
  if (!releaseDate) return null
  return new Date(releaseDate).getFullYear()
})

// Get score overall
const scoreOverallComputed = computed(() => {
  return props.review?.score_overall ?? 5
})

// Handler for image path updates
const onImagePathUpdate = (imagePath) => {
  selectedImagePath.value = imagePath
}

// Export all size variants for a given variant type
const exportAllSizes = async (variant = 'plain') => {
  if (!imageEditorRef.value) return null

  try {
    const blobs = await imageEditorRef.value.exportAllSizes(variant)
    return blobs
  } catch (error) {
    console.error(`Failed to export ${variant} images:`, error)
    return null
  }
}

const closeModal = () => {
  emit('close')
}

const clearFormError = () => {
  formError.value = null
  formErrorMessage.value = null
}

const handleSubmit = async () => {
  if (formBusy.value) return

  formBusy.value = true
  formError.value = null
  formErrorMessage.value = null

  try {
    // Get transform data before saving
    let transformData = null
    let plainImageBlobs = null
    let withTextImageBlobs = null

    if (imageEditorRef.value) {
      transformData = imageEditorRef.value.getCurrentTransform()

      // Export all image variants at all sizes
      plainImageBlobs = await exportAllSizes('plain')
      withTextImageBlobs = await exportAllSizes('with_text')
    }

    // Get the selected image path
    const imagePath = selectedImagePath.value || props.review?.image_path

    if (!imagePath) {
      throw new Error('No image selected')
    }

    // Update only the image_path and image_transform fields
    const updateData = {
      id: props.review.id,
      image_path: imagePath,
      image_transform: transformData
    }

    // Update the review (this automatically refreshes the review data)
    await storeReviews.updateReview(updateData)

    // Upload images in the background
    const reviewId = props.review.id
    if (reviewId && imageEditorRef.value) {
      // Helper to upload all sizes for a variant
      const uploadVariantSizes = async (blobs, variant) => {
        if (!blobs) return

        for (const [sizeName, blob] of Object.entries(blobs)) {
          if (!blob) continue

          try {
            // Use variant_size naming convention (e.g., plain_large, with_text_medium)
            // Large size uses base variant name for backwards compatibility
            const variantWithSize =
              sizeName === 'large' ? variant : `${variant}_${sizeName}`
            await storeReviews.uploadReviewImage(
              reviewId,
              blob,
              variantWithSize,
              transformData
            )
          } catch (uploadError) {
            console.error(
              `Failed to upload ${variant} (${sizeName}):`,
              uploadError
            )
          }
        }
      }

      // Start background uploads (fire and forget)
      Promise.all([
        uploadVariantSizes(plainImageBlobs, 'plain'),
        uploadVariantSizes(withTextImageBlobs, 'with_text')
      ])
        .then(() => {
          // Refresh the review data in the store after uploads complete
          storeReviews.fetchReviewById(reviewId)
        })
        .catch((err) => {
          console.error('Background image upload failed:', err)
        })
    }

    // Close modal on success
    closeModal()
  } catch (error) {
    formError.value = error
    formErrorMessage.value = error?.message || 'Failed to update image'
    formBusy.value = false
  }
}
</script>
